import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchPracticeSummary } from '../../store/slices/practiceSlice';
import EventLogSection from '../../components/EventLogSection/EventLogSection';
import { getPracticeHubPath, isPracticeHubRoute } from '../../utils/practiceNavigation';
import { buildProgramRibbonItems } from '../../utils/careProgramUtils';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { usePracticeDashboardTabIndicator } from '../PracticeManagement/PracticeDashboard/usePracticeDashboardTabIndicator';
import {
  PRACTICE_EVENT_LOGS_QUERY,
  supportsPracticeInlineEventLogs,
} from '../PracticeManagement/PracticeDashboard/practiceEventLogs';
import '../PracticeManagement/PracticeDashboard/PracticeDashboard.css';
import './EventLogsPage.css';

const EVENT_LOG_TABS = [
  { id: 'dashboard', label: 'Practice' },
  { id: 'admin', label: 'Admins' },
  { id: 'provider', label: 'Providers' },
  { id: 'caregiver', label: 'Caregivers' },
  { id: 'patient', label: 'Patients' },
  { id: 'system-caregiver', label: 'System Caregivers' },
  { id: 'rpm', label: 'RPM' },
  { id: 'schedule', label: 'Schedule & Targets' },
  { id: 'import', label: 'Import Patients' }
];

const MODULE_TO_TAB_ID = {
  practice: 'dashboard',
  admin: 'admin',
  provider: 'provider',
  caregiver: 'caregiver',
  patient: 'patient',
  'system caregiver': 'system-caregiver',
  schedule: 'schedule',
  import: 'import',
  rpm: 'rpm'
};

const EventLogsPage = () => {
  const { moduleId, targetUserId } = useParams();
  const [searchParams] = useSearchParams();
  const practiceId = searchParams.get('practiceId');
  const fromDashboardQuery = searchParams.get('fromDashboard') === 'true';
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { user } = useSelector(state => state.auth ?? {});

  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!practiceId) return false;
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });

  const activeTabId = useMemo(() => {
    const key = String(moduleId || '').trim().toLowerCase();
    return MODULE_TO_TAB_ID[key] || 'admin';
  }, [moduleId]);

  const fromDashboard = fromDashboardQuery
    || location.pathname.startsWith('/dashboard')
    || Boolean(location.state?.fromDashboard);

  const { tabsWrapRef, tabIndicatorStyle, indicatorReady } = usePracticeDashboardTabIndicator(
    activeTabId,
    EVENT_LOG_TABS.length
  );

  const sectionFilter = searchParams.get('section');

  useEffect(() => {
    const loadPracticeData = async () => {
      if (!practiceId) {
        setLoading(false);
        return;
      }
      try {
        if (currentPractice && String(currentPractice.id) === String(practiceId)) {
          setPracticeData(currentPractice);
          setLoading(false);
          return;
        }

        setLoading(true);
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        }
      } catch (err) {
        console.error('Error fetching practice summary for logs:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPracticeData();
  }, [practiceId, dispatch, currentPractice]);

  useEffect(() => {
    if (!practiceId || !practiceData) return;
    if (String(targetUserId || '').toLowerCase() !== 'all') return;
    if (!supportsPracticeInlineEventLogs(activeTabId)) return;

    const referrerPathname = (() => {
      try {
        if (typeof document === 'undefined' || !document.referrer) return '';
        return new URL(document.referrer).pathname;
      } catch {
        return '';
      }
    })();
    const canInlineRedirect = fromDashboard
      || isPracticeHubRoute(referrerPathname)
      || Boolean(location.state?.fromDashboard);

    if (!canInlineRedirect) return;

    const hubPath = getPracticeHubPath(practiceId, activeTabId, fromDashboard);
    const [basePath, queryString = ''] = hubPath.split('?');
    const params = new URLSearchParams(queryString);
    if (activeTabId === 'schedule') {
      params.delete(PRACTICE_EVENT_LOGS_QUERY);
      if (sectionFilter) {
        params.set('scheduleLogSection', sectionFilter);
      }
    } else {
      params.set(PRACTICE_EVENT_LOGS_QUERY, '1');
      params.delete('scheduleLogSection');
    }

    navigate(
      `${basePath}?${params.toString()}`,
      {
        replace: true,
        state: fromDashboard ? { fromDashboard: true } : undefined,
      }
    );
  }, [
    practiceId,
    practiceData,
    targetUserId,
    activeTabId,
    fromDashboard,
    location.pathname,
    navigate,
    sectionFilter,
  ]);

  useEffect(() => {
    if (!practiceData) {
      if (!practiceId) {
        if (moduleId === 'Admin') {
          updateBreadcrumbs([
            { label: 'System Admins', path: '/admin', active: false },
            { label: 'Event Logs', path: null, active: true }
          ]);
        } else if (moduleId === 'System Caregiver') {
          updateBreadcrumbs([
            { label: 'System Caregivers', path: '/system-caregiver-dashboard', active: false },
            { label: 'Event Logs', path: null, active: true }
          ]);
        } else {
          updateBreadcrumbs([
            { label: `${moduleId || 'System'} Event Logs`, path: null, active: true }
          ]);
        }
      }
      return;
    }

    const isProvider = Number(user?.role_id) === 4;
    const isPracticeAdmin = Number(user?.role_id) === 3;
    const hideGlobalPracticesCrumb = isProvider || isPracticeAdmin;
    const currentTab = EVENT_LOG_TABS.find((tab) => tab.id === activeTabId);
    const isFromDashboardPath = location.pathname.startsWith('/dashboard')
      || location.state?.fromDashboard
      || fromDashboard;
    const rootLabel = isFromDashboardPath ? 'Dashboard' : 'Practices';
    const rootPath = isFromDashboardPath ? '/dashboard' : '/practices';
    const dashboardState = isFromDashboardPath ? { fromDashboard: true } : undefined;

    const practiceCrumbPath = getPracticeHubPath(practiceId, 'dashboard', isFromDashboardPath);
    const tabCrumbPath = getPracticeHubPath(practiceId, activeTabId, isFromDashboardPath);
    const baseCrumbs = [];

    if (activeTabId === 'dashboard') {
      if (hideGlobalPracticesCrumb) {
        if (isPracticeAdmin) {
          updateBreadcrumbs([
            { label: practiceData.practice_name, path: null, active: false },
            { label: 'Event Logs', path: null, active: true }
          ]);
          return;
        }
        updateBreadcrumbs([
          { label: practiceData.practice_name, path: null, active: false },
          { label: 'Event Logs', path: null, active: true }
        ]);
        return;
      }

      baseCrumbs.push({ label: rootLabel, path: rootPath, active: false });
      baseCrumbs.push({ label: practiceData.practice_name, path: null, active: false });
      baseCrumbs.push({ label: 'Event Logs', path: null, active: true });
      updateBreadcrumbs(baseCrumbs);
      return;
    }

    if (!hideGlobalPracticesCrumb) {
      baseCrumbs.push({ label: rootLabel, path: rootPath, active: false });
    }

    baseCrumbs.push({
      label: practiceData.practice_name,
      path: hideGlobalPracticesCrumb ? null : practiceCrumbPath,
      active: false,
      state: dashboardState
    });
    baseCrumbs.push({
      label: currentTab?.label || 'Practice',
      path: tabCrumbPath,
      active: false,
      state: dashboardState
    });
    baseCrumbs.push({ label: 'Event Logs', path: null, active: true });
    updateBreadcrumbs(baseCrumbs);
  }, [
    practiceData,
    activeTabId,
    practiceId,
    updateBreadcrumbs,
    user,
    location.pathname,
    location.state,
    fromDashboard
  ]);

  useEffect(() => () => clearBreadcrumbs(), [clearBreadcrumbs]);

  const goBack = () => {
    navigate(-1);
  };

  const handleTabClick = (tabId) => {
    if (practiceId) {
      navigate(
        getPracticeHubPath(practiceId, tabId, fromDashboard),
        fromDashboard ? { state: { fromDashboard: true } } : undefined
      );
    } else {
      goBack();
    }
  };

  const renderProgramSummary = () => {
    if (!practiceData) return null;
    const enrolled = Array.isArray(practiceData.enrolled_programs) ? practiceData.enrolled_programs : [];
    if (enrolled.length === 0) {
      return (
        <div className="practice-dashboard-program-ribbon">
          <span className="program-ribbon-text text-muted">No care programs enrolled</span>
        </div>
      );
    }

    const programs = buildProgramRibbonItems(enrolled, practiceData.program_counts || {});

    return (
      <div className="practice-dashboard-program-ribbon">
        {programs.map((prog) => (
          <div key={prog.id} className="program-ribbon-item">
            <FontAwesomeIcon icon={prog.icon} className="program-ribbon-icon" style={{ color: prog.color }} />
            <span className="program-ribbon-text">
              <span className="program-ribbon-count">{prog.count}</span>{' '}
              {prog.short_name || prog.displayName || prog.full_name || prog.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="practice-dashboard-loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (practiceId && !practiceData) {
    return (
      <div className="practice-dashboard-alert practice-dashboard-alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  const title = practiceData
    ? practiceData.practice_name
    : moduleId
      ? `${moduleId} Event Logs`
      : 'System Event Logs';

  return (
    <div className="practice-dashboard">
      <div className="practice-dashboard-single-card">
        <section className="practice-dashboard-card-header">
          <div className="practice-dashboard-header-main">
            <div className="practice-dashboard-header-left">
              <h2 className="practice-dashboard-title">{title}</h2>
              {practiceData && renderProgramSummary()}
            </div>
          </div>
        </section>

        {practiceData && user?.role_id !== 7 && (
          <nav className="practice-dashboard-nav" aria-label="Practice sections">
            <div className="practice-dashboard-tabs-wrap" ref={tabsWrapRef}>
              <span
                className={`practice-dashboard-tab-indicator${indicatorReady ? ' practice-dashboard-tab-indicator--ready' : ''}`}
                style={tabIndicatorStyle}
                aria-hidden="true"
              />
              <ul className="practice-dashboard-tabs">
                {EVENT_LOG_TABS.map((tab) => (
                  <li key={tab.id} className={`tab-item ${activeTabId === tab.id ? 'active' : ''}`}>
                    <button
                      onClick={() => handleTabClick(tab.id)}
                      className="tab-button"
                      type="button"
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        )}

        <section className="practice-dashboard-card-body practice-dashboard-card-body--event-logs">
          <div className="event-logs-content">
            <EventLogSection
              moduleId={moduleId}
              targetUserId={targetUserId}
              practiceId={practiceId}
              sectionFilter={sectionFilter}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default EventLogsPage;
